/**
 * 🧾 InvoicesTab — Onglet Factures (Phase 2)
 * ============================================
 *
 * Liste les factures B2C de l'utilisateur (AMSY E-COM / COACH DIA),
 * regroupées par année dans des sections dépliables.
 *
 * Comportement :
 *   - Année courante ouverte par défaut
 *   - Années précédentes repliées par défaut
 *   - Tri décroissant par année + tri décroissant par date
 *   - Téléchargement sécurisé avec ownership check serveur
 *   - État vide élégant
 *   - Disponibilité 10 ans
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  FileText, Download, ChevronDown, Loader2, Receipt,
  Sparkles, CheckCircle2, Mail, Search, Archive,
} from 'lucide-react';
import { getBackendUrl } from '../../config/environment';

const BACKEND_URL = getBackendUrl();

// 🎨 Palette premium ivoire/violet/or
const T = {
  ink: '#1C1147',
  inkSoft: '#3A2E6E',
  gold: '#D4AF37',
  goldDeep: '#B7901F',
  goldLight: '#F3D060',
  ivory: '#FBF9F3',
  ivoryDeep: '#F4F1E8',
  border: '#E8E5DE',
  text: '#374151',
  muted: '#6B7280',
  green: '#48825A',
};

const formatEUR = (n) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return '—';
  return n
    .toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    + ' €';
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
};

const PAYMENT_TYPE_LABEL = {
  subscription_monthly: 'Abonnement mensuel',
  one_shot: 'Achat unique',
  recharge: 'Recharge sessions',
  coaching_session: 'Session coaching humain',
};

function PaymentTypeBadge({ type }) {
  const label = PAYMENT_TYPE_LABEL[type] || 'Paiement';
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
      style={{
        background: `${T.ink}10`,
        color: T.inkSoft,
        border: `1px solid ${T.ink}22`,
      }}
      data-testid="invoice-type-badge"
    >
      {label}
    </span>
  );
}

function PaidBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
      style={{
        background: `${T.green}18`,
        color: T.green,
        border: `1px solid ${T.green}55`,
      }}
      data-testid="invoice-paid-badge"
    >
      <CheckCircle2 className="w-3 h-3" />
      Payée
    </span>
  );
}

function InvoiceRow({ invoice, onDownload, onResend, downloading, resending, isArchived }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      data-testid={`invoice-row-${invoice.invoice_number}`}
      className="rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 transition-all hover:shadow-md"
      style={{
        background: '#FFFFFF',
        border: `1px solid ${T.border}`,
      }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span
            className="text-[11px] font-bold uppercase tracking-[0.14em]"
            style={{ color: T.goldDeep }}
          >
            {invoice.invoice_number}
          </span>
          <PaymentTypeBadge type={invoice.payment_type} />
          <PaidBadge />
          {isArchived && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
              style={{
                background: `${T.muted}18`,
                color: T.muted,
                border: `1px solid ${T.muted}55`,
              }}
              data-testid="invoice-archived-badge"
              title="Facture de plus d'un an — toujours téléchargeable"
            >
              <Archive className="w-3 h-3" />
              Archivée
            </span>
          )}
        </div>
        <div
          className="text-sm font-medium leading-snug truncate"
          style={{ color: T.ink }}
          title={invoice.line_description}
        >
          {invoice.line_description || 'Prestation COACH DIA'}
        </div>
        <div
          className="mt-0.5 text-[11px]"
          style={{ color: T.muted }}
        >
          Émise le {formatDate(invoice.issued_at)}
          {invoice.email_sent_at && (
            <> · <Mail className="inline w-3 h-3 -mt-0.5" /> Envoyée le{' '}
              {formatDate(invoice.email_sent_at)}</>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 sm:flex-shrink-0">
        <div className="text-right pr-1">
          <div
            className="text-[10px] uppercase tracking-[0.14em] font-bold"
            style={{ color: T.muted }}
          >
            TTC
          </div>
          <div
            className="text-base font-bold tabular-nums"
            style={{ color: T.ink }}
          >
            {formatEUR(invoice.amount_ttc)}
          </div>
        </div>
        <button
          type="button"
          data-testid={`resend-invoice-btn-${invoice.invoice_number}`}
          onClick={() => onResend(invoice)}
          disabled={resending}
          title="Renvoyer cette facture par email"
          className="inline-flex items-center justify-center w-9 h-9 rounded-lg transition-all hover:opacity-90 disabled:opacity-50"
          style={{
            background: '#FFFFFF',
            border: `1px solid ${T.border}`,
            color: T.inkSoft,
          }}
        >
          {resending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Mail className="w-3.5 h-3.5" />
          )}
        </button>
        <button
          type="button"
          data-testid={`download-invoice-btn-${invoice.invoice_number}`}
          onClick={() => onDownload(invoice)}
          disabled={downloading}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold transition-all hover:opacity-90 disabled:opacity-50"
          style={{
            background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold})`,
            color: T.ink,
          }}
        >
          {downloading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Download className="w-3.5 h-3.5" />
          )}
          Télécharger
        </button>
      </div>
    </motion.div>
  );
}

function YearSection({ year, count, invoices, isOpen, onToggle, onDownload, onResend, downloadingId, resendingId, archiveCutoff }) {
  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: T.ivory,
        border: `1px solid ${T.border}`,
      }}
      data-testid={`year-section-${year}`}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`year-toggle-${year}`}
        className="w-full px-4 py-3 flex items-center justify-between transition-colors hover:bg-[#F4F1E8]"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${T.gold}, ${T.goldLight})`,
              color: T.ink,
            }}
          >
            <Receipt className="w-4 h-4" />
          </div>
          <div className="text-left">
            <div
              className="font-bold text-base"
              style={{ color: T.ink, fontFamily: 'serif' }}
            >
              {year}
            </div>
            <div className="text-[11px]" style={{ color: T.muted }}>
              {count} facture{count > 1 ? 's' : ''}
            </div>
          </div>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="w-4 h-4" style={{ color: T.inkSoft }} />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 flex flex-col gap-2">
              {invoices.map((inv) => {
                const issuedDate = inv.issued_at ? new Date(inv.issued_at) : null;
                const isArchived =
                  issuedDate && archiveCutoff && issuedDate < archiveCutoff;
                return (
                  <InvoiceRow
                    key={inv.invoice_id}
                    invoice={inv}
                    onDownload={onDownload}
                    onResend={onResend}
                    downloading={downloadingId === inv.invoice_id}
                    resending={resendingId === inv.invoice_id}
                    isArchived={isArchived}
                  />
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      className="rounded-2xl px-8 py-12 flex flex-col items-center text-center gap-3"
      style={{
        background: T.ivory,
        border: `1px dashed ${T.gold}66`,
      }}
      data-testid="invoices-empty-state"
    >
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center"
        style={{
          background: `linear-gradient(135deg, ${T.gold}22, ${T.goldLight}33)`,
          color: T.goldDeep,
        }}
      >
        <FileText className="w-7 h-7" />
      </div>
      <div
        className="font-serif text-lg font-bold"
        style={{ color: T.ink }}
      >
        Aucune facture pour le moment
      </div>
      <div
        className="text-sm max-w-md"
        style={{ color: T.muted }}
      >
        Vos factures apparaîtront ici dès votre prochain paiement
        (abonnement, recharge ou session de coaching humain).
        Elles resteront accessibles pendant 10 ans.
      </div>
    </div>
  );
}

export default function InvoicesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openYears, setOpenYears] = useState(new Set());
  const [downloadingId, setDownloadingId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Cutoff archivage UI : factures > 1 an = badge "Archivée"
  const archiveCutoff = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d;
  }, []);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/invoices/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      if (json.years?.length) {
        const currentYear = json.current_year ?? new Date().getFullYear();
        const initial = new Set();
        if (json.years.some((y) => y.year === currentYear)) {
          initial.add(currentYear);
        } else {
          initial.add(json.years[0].year);
        }
        setOpenYears(initial);
      }
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const toggleYear = (year) => {
    setOpenYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  };

  const handleDownload = async (invoice) => {
    setDownloadingId(invoice.invoice_id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${BACKEND_URL}/api/invoices/${invoice.invoice_id}/download`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Facture téléchargée');
    } catch (e) {
      toast.error(`Téléchargement impossible : ${e.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleResend = async (invoice) => {
    setResendingId(invoice.invoice_id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${BACKEND_URL}/api/invoices/${invoice.invoice_id}/resend-email`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP ${res.status}`);
      }
      toast.success(`Facture renvoyée à ${invoice.customer_email || 'votre email'}`);
      // Refresh pour actualiser email_sent_at
      fetchInvoices();
    } catch (e) {
      toast.error(`Envoi impossible : ${e.message}`);
    } finally {
      setResendingId(null);
    }
  };

  // Filtrage côté client (type + recherche numéro)
  const filteredYears = useMemo(() => {
    if (!data?.years) return [];
    const q = searchQuery.trim().toLowerCase();
    return data.years
      .map((y) => {
        const filtered = y.invoices.filter((inv) => {
          if (filterType !== 'all' && inv.payment_type !== filterType) return false;
          if (q && !(inv.invoice_number || '').toLowerCase().includes(q)) return false;
          return true;
        });
        return filtered.length > 0
          ? { ...y, invoices: filtered, count: filtered.length }
          : null;
      })
      .filter(Boolean);
  }, [data, filterType, searchQuery]);

  if (loading) {
    return (
      <div
        className="rounded-2xl p-12 flex items-center justify-center"
        style={{ background: T.ivory, border: `1px solid ${T.border}` }}
        data-testid="invoices-loading"
      >
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: T.goldDeep }} />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-2xl p-6 text-sm"
        style={{
          background: '#FFF7ED',
          border: '1px solid #FED7AA',
          color: '#9A3412',
        }}
        data-testid="invoices-error"
      >
        Une erreur est survenue : {error}
      </div>
    );
  }

  const hasInvoices = data && data.total > 0;
  const filters = [
    { key: 'all', label: 'Toutes' },
    { key: 'subscription_monthly', label: 'Abonnement' },
    { key: 'coaching_session', label: 'Coaching' },
    { key: 'recharge', label: 'Recharge' },
    { key: 'one_shot', label: 'Achat unique' },
  ];

  return (
    <div className="flex flex-col gap-4" data-testid="invoices-tab-content">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <div
            className="text-[10px] uppercase tracking-[0.20em] font-bold"
            style={{ color: T.goldDeep }}
          >
            AMSY E-COM · COACH DIA
          </div>
          <h2
            className="font-serif text-2xl font-bold mt-0.5"
            style={{ color: T.ink }}
          >
            Mes factures
          </h2>
          <div
            className="inline-flex items-center gap-1.5 mt-1.5 text-[12px]"
            style={{ color: T.muted }}
          >
            <Sparkles className="w-3 h-3" style={{ color: T.goldDeep }} />
            Disponibles pendant 10 ans · Téléchargement illimité
          </div>
        </div>
        {hasInvoices && (
          <div
            className="text-[11px] font-semibold px-3 py-1.5 rounded-full"
            style={{
              background: `${T.ink}08`,
              color: T.inkSoft,
              border: `1px solid ${T.border}`,
            }}
            data-testid="invoices-total-count"
          >
            {data.total} facture{data.total > 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Filtres + recherche (visibles dès qu'il y a au moins 1 facture) */}
      {hasInvoices && (
        <div
          className="flex flex-col lg:flex-row gap-2 lg:items-center"
          data-testid="invoices-filters"
        >
          <div className="flex flex-wrap items-center gap-1.5 flex-1">
            {filters.map((f) => (
              <button
                key={f.key}
                type="button"
                data-testid={`filter-${f.key}-btn`}
                onClick={() => setFilterType(f.key)}
                className="px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all"
                style={{
                  background:
                    filterType === f.key
                      ? `linear-gradient(135deg, ${T.ink}, #2A1A6E)`
                      : '#FFFFFF',
                  color: filterType === f.key ? T.goldLight : T.inkSoft,
                  border: `1px solid ${
                    filterType === f.key ? T.ink : T.border
                  }`,
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-full"
            style={{
              background: '#FFFFFF',
              border: `1px solid ${T.border}`,
              minWidth: 220,
            }}
          >
            <Search className="w-3.5 h-3.5" style={{ color: T.muted }} />
            <input
              data-testid="invoices-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Numéro de facture..."
              className="flex-1 bg-transparent outline-none text-[12px]"
              style={{ color: T.ink }}
            />
          </div>
        </div>
      )}

      {/* Body */}
      {!hasInvoices ? (
        <EmptyState />
      ) : filteredYears.length === 0 ? (
        <div
          className="rounded-2xl p-8 text-center text-sm"
          style={{ background: T.ivory, border: `1px dashed ${T.border}`, color: T.muted }}
          data-testid="invoices-no-match"
        >
          Aucune facture ne correspond à votre recherche.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filteredYears.map((y) => (
            <YearSection
              key={y.year}
              year={y.year}
              count={y.count}
              invoices={y.invoices}
              isOpen={openYears.has(y.year)}
              onToggle={() => toggleYear(y.year)}
              onDownload={handleDownload}
              onResend={handleResend}
              downloadingId={downloadingId}
              resendingId={resendingId}
              archiveCutoff={archiveCutoff}
            />
          ))}
        </div>
      )}

      {/* Mention légale émetteur */}
      <div
        className="rounded-xl px-4 py-3 text-[11px] leading-relaxed"
        style={{
          background: T.ivoryDeep,
          border: `1px solid ${T.gold}33`,
          color: T.inkSoft,
        }}
      >
        Factures émises par <b>AMSY E-COM SAS</b> pour l'utilisation de la plateforme{' '}
        <b>COACH DIA</b>. Toute facture est conservée conformément à la législation française.
      </div>
    </div>
  );
}
